import React, { PropsWithChildren, useState } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import {
    IAddOrEditEquipment,
    IEquipment,
    IEquipmentFormComponent as IEquipmentFormComponent,
} from '../../equipment.interfaces';
import formModels from './formModel/equipment.form.model';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { formInitialValues } from './formModel/equipment.form.initial.values';
import Button from '@material-ui/core/Button';
import en from '../../../../../assets/language/en.json';
import Dialog from '@material-ui/core/Dialog';
import { Form, Formik, FormikProps } from 'formik';
import validationSchemas from './formModel/validation.schema';
import { FormControl, FormGroup, FormHelperText, TextField, InputAdornment } from '@material-ui/core';
import { equipmentActions } from '../../equipment.actions';
import styles from './index.module.scss';
import { compose } from 'recompose';
import { muiStyles } from '../../../../shared/styles/mui.styles';
import clsx from 'clsx';
import LanguageFlag from '../../../../shared/components/generics/languageFlag/LanguageFlag';
import { useParams } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { equipmentConstants } from '../../equipment.constants';
import { useIdParam } from '../../../../shared/helpers/custom.hooks';

type EquipmentProps = IEquipmentFormComponent & typeof formModels.equipmentFormModel;

const DialogTitle = withStyles(muiStyles.addOrEditDialog)((props: IAddOrEditEquipment) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(muiStyles.dialogContent)(MuiDialogContent);

const DialogActions = withStyles(muiStyles.dialogActions)(MuiDialogActions);

const AddOrEditEquipment = (props: PropsWithChildren<EquipmentProps>): JSX.Element => {
    const {
        formField: { nameEn, nameDe },
        equipment: equipment,
        history,
    } = props;
    const id = useIdParam();

    const [open, setOpen] = useState<boolean>(!!(id && id == equipment?.id));

    const formValues = equipment ? equipment : formInitialValues.equipment;

    const handleClickOpen = () => {
        setOpen(true);
        equipment && history.push(`${equipmentConstants.EQUIPMENT_ROUTES.EQUIPMENT}/${equipment.id}`);
    };

    const handleClose = () => {
        setOpen(false);
        history.push(`${equipmentConstants.EQUIPMENT_ROUTES.EQUIPMENT}`);
    };

    const handleSubmit = (values: IEquipment): void => {
        equipment ? handleUpdate(values) : handleInsert(values);
    };

    const handleInsert = (values: IEquipment): void => {
        props.addEquipment(values).then(() => {
            props.refreshData();
            handleClose();
        });
    };

    const handleUpdate = (values: IEquipment): void => {
        const equipmentToUpdate: IEquipment = {
            nameEn: values.nameEn,
            nameDe: values.nameDe,
        };
        props.updateEquipment(equipmentToUpdate, values.id).then(() => {
            props.refreshData();
            handleClose();
        });
    };

    return (
        <div className={styles.equipment}>
            {equipment ? (
                <Button className={styles.editEquipment} onClick={handleClickOpen}>
                    {en.edit_button_label}
                </Button>
            ) : (
                <Button className={clsx(styles.addEquipment, 'mb4')} onClick={handleClickOpen}>
                    {en.add_new_equipment}
                </Button>
            )}

            <Dialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                maxWidth={'xs'}
                fullWidth={true}
            >
                <DialogTitle id="customized-dialog-title" onClose={handleClose}>
                    {equipment ? en.edit_equipment : en.add_new_equipment}
                </DialogTitle>
                <Formik
                    initialValues={formValues}
                    onSubmit={(values: IEquipment, { setSubmitting }) => {
                        handleSubmit(values);
                        setSubmitting(false);
                    }}
                    validationSchema={validationSchemas.equipmentValidationSchema}
                    enableReinitialize={true}
                >
                    {(props: FormikProps<IEquipment>) => (
                        <Form className={styles.addEquipmentForm}>
                            <DialogContent dividers>
                                <FormGroup className={styles.inputGroup}>
                                    <FormControl className={clsx(styles.formControl, 'mb4')} required>
                                        <TextField
                                            className={styles.formInput}
                                            name={nameEn.name}
                                            label={nameEn.label}
                                            type="text"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.nameEn}
                                            variant={'outlined'}
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LanguageFlag country="us" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <FormHelperText className={clsx(styles.error_span, 'mt2')}>
                                            {props.errors.nameEn && props.touched.nameEn ? props.errors.nameEn : ''}
                                        </FormHelperText>
                                    </FormControl>
                                    <FormControl className={clsx(styles.formControl, 'mb4')} required>
                                        <TextField
                                            className={styles.formInput}
                                            name={nameDe.name}
                                            label={nameDe.label}
                                            type="text"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.nameDe}
                                            variant={'outlined'}
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LanguageFlag country="de" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <FormHelperText className={clsx(styles.error_span, 'mt2')}>
                                            {props.errors.nameEn && props.touched.nameEn ? props.errors.nameEn : ''}
                                        </FormHelperText>
                                    </FormControl>
                                </FormGroup>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    type="submit"
                                    className={clsx(styles.saveEquipment, 'mb4')}
                                    disabled={props.isSubmitting || !(props.isValid && props.dirty)}
                                >
                                    {en.save_button_label}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

const mapDispatchToProps = {
    addEquipment: equipmentActions.addEquipment,
    updateEquipment: equipmentActions.updateEquipment,
};

export default compose(withRouter, connect(null, mapDispatchToProps))(AddOrEditEquipment);
